import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Download, FileText, Pencil, Plus, Trash2, Upload, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { apiError, fileUrl } from "./api";
import { useAuth } from "./QcApp";
import { STAGE_LABELS, STAGE_STYLES } from "./FlowList";
import { VendorFormDialog } from "./Vendors";

const STAGES = ["qc", "commercial", "unit_head", "accounts", "closed"];
const money = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

const DOC_TEMPLATES = [
  ["form_a2", "Form A2"],
  ["bank_letter", "Bank Letter"],
  ["fema", "FEMA Declaration"],
];

const DOC_FIELD_LISTS = {
  form_a2: ["applicant_name", "applicant_pan", "applicant_address", "our_bank_full", "our_account_no", "beneficiary_name", "beneficiary_address", "beneficiary_bank_name", "beneficiary_country", "beneficiary_account_no", "invoice_no", "invoice_date", "financial_year", "test_description", "currency", "currency_abbr", "net_amount_numeric", "net_amount_words"],
  bank_letter: ["bl_document_date", "our_bank_name", "our_bank_branch", "our_bank_city", "applicant_name", "applicant_pan", "our_account_no", "place", "beneficiary_name", "beneficiary_address", "beneficiary_country", "beneficiary_bank_name", "beneficiary_swift_bic", "beneficiary_account_no", "beneficiary_iban", "beneficiary_bcn", "currency_abbr", "net_amount_numeric", "bl_net_amount_words", "test_description", "invoice_no", "invoice_date"],
  fema: ["document_date", "place", "test_description", "currency", "net_amount_numeric", "net_amount_words", "invoice_no", "invoice_date", "beneficiary_name"],
};

const FIELD_LABELS = {
  applicant_name: "Applicant name",
  applicant_pan: "Applicant PAN",
  applicant_address: "Applicant address",
  our_bank_full: "Our bank (single line)",
  our_bank_name: "Our bank name",
  our_bank_branch: "Our bank branch",
  our_bank_city: "Our bank city",
  our_account_no: "Our debit account no.",
  place: "Place (signing location)",
  reference_no: "Reference No.",
  document_date: "Document date",
  bl_document_date: "Document date (Bank Letter)",
  financial_year: "Financial year",
  beneficiary_name: "Beneficiary name",
  beneficiary_address: "Beneficiary address",
  beneficiary_country: "Beneficiary country",
  beneficiary_bank_name: "Beneficiary bank name",
  beneficiary_swift_bic: "Beneficiary bank SWIFT/BIC",
  beneficiary_account_no: "Beneficiary account no.",
  beneficiary_iban: "Beneficiary IBAN",
  beneficiary_bcn: "Beneficiary BCN / routing code",
  invoice_no: "Invoice No.(s)",
  invoice_date: "Invoice Date(s)",
  test_description: "Test / service description",
  currency: "Currency (in words, e.g. Euro)",
  currency_abbr: "Currency code (e.g. EUR)",
  gross_amount: "Gross amount",
  tds_percent: "TDS % (label)",
  tds_amount: "TDS amount",
  net_amount_numeric: "Net remittance value",
  net_amount_words: "Net amount in words",
  bl_net_amount_words: "Net amount in words (Bank Letter, with currency prefix)",
};

const WIDE_FIELDS = ["applicant_address", "beneficiary_address", "test_description", "net_amount_words", "bl_net_amount_words", "our_bank_full", "applicant_name"];

// Turns digit-only entries like 29092026, 292026 or 290926 into a formatted date (29/09/2026, or with the given separator).
const autoDate = (v, sep = "/") => {
  const d = (v || "").replace(/\D/g, "");
  if (d.length === 8) return `${d.slice(0, 2)}${sep}${d.slice(2, 4)}${sep}${d.slice(4)}`;
  if (d.length === 7) return `0${d.slice(0, 1)}${sep}${d.slice(1, 3)}${sep}${d.slice(3)}`;
  if (d.length === 6) return `${d.slice(0, 2)}${sep}${d.slice(2, 4)}${sep}20${d.slice(4)}`;
  if (d.length === 5) return `0${d.slice(0, 1)}${sep}${d.slice(1, 3)}${sep}20${d.slice(3)}`;
  return v;
};

const Card = ({ title, sub, children, testid }) => (
  <div className="rounded-2xl border border-[#E8E6E1] bg-white p-6" data-testid={testid}>
    <h2 className="font-display text-lg font-semibold">{title}</h2>
    {sub && <p className="mb-4 mt-0.5 text-sm text-[#8A9A91]">{sub}</p>}
    <div className={sub ? "" : "mt-4"}>{children}</div>
  </div>
);

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [editInv, setEditInv] = useState(null);
  const [vendorDialog, setVendorDialog] = useState({ open: false, initial: null, pickOnly: false });
  const [tdsDocsOpen, setTdsDocsOpen] = useState(false);
  const [docsEditOpen, setDocsEditOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const uploadRef = useRef(null);
  const tdsFileRef = useRef(null);
  const [tdsRemarkNew, setTdsRemarkNew] = useState("");
  const caRef = useRef(null);
  const ca2Ref = useRef(null);
  const adviceRef = useRef(null);
  const [, forceCloseCheck] = useState(0);

  const [tdsId, setTdsId] = useState("");
  const [tdsDocType, setTdsDocType] = useState("");
  const [tdsRemark, setTdsRemark] = useState("");
  const [discount, setDiscount] = useState("");
  const [testDesc, setTestDesc] = useState("");
  const [bankAccId, setBankAccId] = useState("");
  const [ov, setOv] = useState({});
  const [docChoice, setDocChoice] = useState("form_a2");
  const [docForm, setDocForm] = useState({});
  const [products, setProducts] = useState([]);
  const [payForm, setPayForm] = useState({ date: "", ref: "", amount: "", remark: "" });

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const load = () =>
    api.get(`/cases/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
  }, [id]);

  const c = data?.case;
  const vendor = data?.vendor;
  const tdsOptions = data?.tds_options || [];

  useEffect(() => {
    if (!c) return;
    setTdsId(c.tds_id || "");
    setTdsDocType(c.tds_doc_type || "");
    setTdsRemark(c.tds_remark || "");
    setDiscount(c.discount_pct ?? "");
    setTestDesc(
      c.test_description ||
        c.invoices
          .flatMap((i) => (i.items?.length ? i.items.map((it) => it.description) : [i.description]))
          .filter(Boolean)
          .join(", ")
    );
    const def = vendor?.bank_accounts?.find((b) => b.is_default) || vendor?.bank_accounts?.[0];
    setBankAccId(c.bank_account_id || def?.id || "");
    setOv(c.doc_overrides || {});
  }, [data]);

  const calc = useMemo(() => {
    if (!c) return null;
    const gross = c.invoices.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const d = parseFloat(discount) || 0;
    const opt = tdsOptions.find((t) => t.id === tdsId);
    const rate = opt?.rate || 0;
    if (c.flow === "domestic") {
      const base = c.invoices.reduce((s, i) => s + (parseFloat(i.total_before_gst) || parseFloat(i.amount) || 0), 0);
      const gst = c.invoices.reduce((s, i) => s + (parseFloat(i.gst_charged) || 0), 0);
      const tds = Math.ceil(Math.round(base * (rate / 100) * 100) / 100);
      return { gross, base, gst, subtotal: base, tds, net: base - tds + gst, opt };
    }
    const subtotal = gross * (1 - d / 100);
    const tds = subtotal * (rate / 100);
    return { gross, subtotal, tds, net: subtotal - tds, opt };
  }, [c, discount, tdsId, tdsOptions]);

  useEffect(() => {
    if (!docsEditOpen || !data) return;
    const defaults = data.doc_defaults || {};
    const overrides = data.case.doc_overrides || {};
    setDocForm(
      Object.fromEntries(DOC_FIELD_LISTS[docChoice].map((f) => [f, overrides[f] ?? defaults[f] ?? ""]))
    );
  }, [docsEditOpen, docChoice, data]);

  if (!data) {
    return <p className="text-sm text-[#8A9A91]">Loading case…</p>;
  }

  const roles = user.roles || [];
  const has = (r) => roles.includes(r);
  const isExport = c.flow === "export";
  const stageIdx = STAGES.indexOf(c.stage);
  const caseAgeMs = Date.now() - new Date(c.created_at).getTime();
  const within24h = caseAgeMs < 24 * 3600 * 1000;
  const qcEditable =
    c.stage === "qc" && (has("admin") || (has("qc") && c.created_by === user.id && within24h));
  const canDeleteCase =
    has("admin") || (c.stage === "qc" && has("qc") && c.created_by === user.id && within24h);
  const commercialEditable =
    (c.stage === "commercial" && has("commercial")) ||
    (["commercial", "unit_head", "accounts"].includes(c.stage) && has("admin"));
  const docsEditable = has("commercial") || has("unit_head") || has("accounts") || has("admin");
  const canGenerate = docsEditable && ["commercial", "unit_head", "accounts"].includes(c.stage);
  const canApprove = c.stage === "commercial" && (has("commercial") || has("admin"));
  const canUnitHeadAct = c.stage === "unit_head" && (has("unit_head") || has("admin"));
  const canClose = c.stage === "accounts" && (has("accounts") || has("admin"));
  const formsSaved = !!(c.closing_docs?.form_145 && c.closing_docs?.form_146);
  const accountsInvEditable = !isExport && c.stage === "accounts" && (has("accounts") || has("admin"));
  const domBase = c.base_total ?? calc?.base ?? c.gross;
  const domGst = c.gst_total ?? calc?.gst ?? 0;
  const selectedTds = tdsOptions.find((t) => t.id === tdsId);
  const needsTdsDoc = isExport && Number(selectedTds?.rate) === 10.4;

  const payAmount = parseFloat(payForm.amount);
  const netPayable = c.net || 0;
  const payMismatch = payForm.amount !== "" && !isNaN(payAmount) && Math.abs(payAmount - netPayable) > 0.009;
  const remarkShort = payMismatch && payForm.remark.trim().length < 15;
  const payValid =
    payForm.date.trim() !== "" &&
    payForm.ref.trim() !== "" &&
    payForm.amount !== "" &&
    !isNaN(payAmount) &&
    !remarkShort;

  const act = async (key, fn, success) => {
    setBusy(key);
    try {
      await fn();
      if (success) toast.success(success);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const uploadInvoice = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: inv } = await api.post(`/cases/${id}/invoices`, fd);
      toast.success("Invoice uploaded — fields pre-filled where detected. Please confirm them.");
      await load();
      setEditInv({ ...inv, currency: c.currency });
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const REQ = <span className="ml-0.5 font-semibold text-red-600">*</span>;

  const saveInvoice = async () => {
    const missing = [];
    if (!editInv.invoice_no?.trim()) missing.push("Invoice No.");
    if (!editInv.invoice_date?.trim()) missing.push("Invoice Date");
    if (!isExport) {
      if (editInv.total_before_gst === "" || editInv.total_before_gst == null) missing.push("Total Amount (before GST)");
      if (editInv.gst_charged === "" || editInv.gst_charged == null) missing.push("GST Charged");
    }
    if (editInv.amount === "" || editInv.amount == null) missing.push(isExport ? "Amount" : "Grand Total");
    const invItems = editInv.items || [];
    if (invItems.length === 0 || invItems.some((it) => !it.product_name?.trim())) missing.push("Product Name (every product row)");
    if (invItems.length === 0 || invItems.some((it) => !it.description?.trim())) missing.push("Test Name (every product row)");
    if (!editInv.order_ref_name?.trim()) missing.push("Order/Reference Name");
    if (!editInv.order_ref_date?.trim()) missing.push("Order/Reference Date");
    if (missing.length > 0) {
      toast.error(`Mandatory fields missing: ${missing.join(", ")}`);
      return;
    }
    try {
      const first = (editInv.items || [])[0] || {};
      await api.put(`/cases/${id}/invoices/${editInv.id}`, {
        ...editInv,
        currency: isExport ? editInv.currency || c.currency : null,
        product_name: first.product_name || "",
        batch_ref: first.batch_ref || "",
        description: first.description || "",
        amount: editInv.amount === "" || editInv.amount == null ? null : parseFloat(editInv.amount),
        total_before_gst: editInv.total_before_gst === "" || editInv.total_before_gst == null ? null : parseFloat(editInv.total_before_gst),
        gst_charged: editInv.gst_charged === "" || editInv.gst_charged == null ? null : parseFloat(editInv.gst_charged),
      });
      toast.success("Invoice saved");
      setEditInv(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveCommercial = () =>
    act("save", () =>
      api.put(`/cases/${id}/commercial`, {
        tds_id: tdsId || null,
        tds_remark: tdsRemark || null,
        tds_doc_type: needsTdsDoc ? tdsDocType || null : null,
        discount_pct: isExport && discount !== "" ? parseFloat(discount) : null,
        test_description: testDesc,
        bank_account_id: bankAccId || null,
      }), "Saved");

  const addTdsDoc = async () => {
    const file = tdsFileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a document file");
    if (!tdsRemarkNew.trim()) return toast.error("Add a remark for this document");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("remark", tdsRemarkNew.trim());
    try {
      await api.post(`/cases/${id}/tds-docs`, fd);
      toast.success("Document added");
      tdsFileRef.current.value = "";
      setTdsRemarkNew("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveOverrides = () =>
    act("ov", async () => {
      const defaults = data.doc_defaults || {};
      const payload = {};
      for (const [k, v] of Object.entries(docForm)) {
        payload[k] = (v || "") === (defaults[k] ?? "") ? "" : v;
      }
      await api.put(`/cases/${id}/doc-overrides`, payload);
      setDocsEditOpen(false);
    }, "Document fields saved — regenerate the documents to apply");

  const closeCase = () =>
    act("close", async () => {
      const fd = new FormData();
      if (isExport) {
        fd.append("bank_payment_advice", adviceRef.current.files[0]);
      } else {
        fd.append("payment_date", payForm.date.trim());
        fd.append("payment_ref", payForm.ref.trim());
        fd.append("payment_amount", payAmount);
        fd.append("payment_remark", payForm.remark.trim());
      }
      await api.post(`/cases/${id}/close`, fd);
    }, "Case closed");

  const saveClosingForms = () =>
    act("saveforms", async () => {
      const fd = new FormData();
      fd.append("form_145", caRef.current.files[0]);
      fd.append("form_146", ca2Ref.current.files[0]);
      await api.post(`/cases/${id}/closing-docs`, fd);
    }, "CA Certificate forms saved — you can close the case later once the Bank Payment Advice is available");

  const vendorEditableByQc =
    vendor && has("qc") && vendor.created_by === user.id &&
    Date.now() - new Date(vendor.created_at).getTime() < 24 * 3600 * 1000;
  const canEditVendorRecord = has("admin") || vendorEditableByQc;
  // QC may re-point the case to a different vendor (name only) while the case is with QC,
  // even when the vendor record itself is outside their 24h edit window
  const qcPickOnly = has("qc") && !has("admin") && c.stage === "qc" && !vendorEditableByQc;
  const vendorEditBlocked = !canEditVendorRecord && !qcPickOnly && (has("commercial") || has("unit_head"));

  const docLinks = c.docs?.combined
    ? [["combined", "Combined PDF (all 3 forms)"], ["form_a2", "Form A2"], ["bank_letter", "Bank Letter"], ["fema", "FEMA Declaration"]]
    : c.docs?.form_a2
      ? [["form_a2", "Form A2"], ["bank_letter", "Bank Letter"], ["fema", "FEMA Declaration"]]
      : [];

  return (
    <div className="space-y-6" data-testid="qc-case-detail">
      <div>
        <Link to={`/qc/invoicing/${c.flow}`} className="mb-2 inline-flex items-center gap-1 text-sm text-[#8A9A91] hover:text-[#1C3F2B]">
          <ArrowLeft className="h-4 w-4" /> {isExport ? "International" : "Domestic"} Invoicing
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-3xl font-semibold tracking-tight" data-testid="qc-case-no">{c.case_no}</h1>
          <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${STAGE_STYLES[c.stage]}`} data-testid="qc-case-stage">
            {STAGE_LABELS[c.stage]}
          </span>
          <span className="text-sm text-[#8A9A91]">{isExport ? `Export · ${c.currency}` : "Domestic · INR"}</span>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {STAGES.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <button
                type="button"
                data-testid={`qc-stage-chip-${s}`}
                title={i === stageIdx ? "You are here — scroll to the Action section to proceed" : STAGE_LABELS[s]}
                onClick={() => document.querySelector('[data-testid="qc-action-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}
                className={`cursor-pointer rounded-full px-3 py-1 text-[11px] font-semibold transition-transform hover:scale-105 ${
                i < stageIdx ? "bg-[#1C3F2B] text-[#F9F8F6]" : i === stageIdx ? STAGE_STYLES[s] + " ring-1 ring-current" : "bg-[#E8E6E1]/60 text-[#8A9A91]"
              }`}
              >
                {STAGE_LABELS[s]}
              </button>
              {i < STAGES.length - 1 && <span className="text-[#8A9A91]">→</span>}
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Invoices" sub={qcEditable ? "Upload the lab invoice first — invoice no., date and amount are pre-filled where detected, and you can correct them." : accountsInvEditable ? "Uploaded by QC. You can correct invoice details if you find any fault — totals and the BILL PASSED stamp update automatically." : "Uploaded by QC. Internal batch/order references never appear on bank documents."} testid="qc-invoices-card">
          <ul className="space-y-3">
            {c.invoices.map((inv) => (
              <li key={inv.id} className="rounded-xl border border-[#E8E6E1] p-4" data-testid={`qc-invoice-${inv.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <a href={fileUrl(inv.storage_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1C3F2B] underline underline-offset-2">
                      <FileText className="h-4 w-4" /> {inv.file_name}
                    </a>
                    <p className="mt-1 text-sm">No. {inv.invoice_no || "—"} · {inv.invoice_date || "—"} · <strong>{c.currency} {money(inv.amount)}</strong></p>
                    {!isExport && (inv.total_before_gst != null || inv.gst_charged != null) && (
                      <p className="mt-0.5 text-xs text-[#8A9A91]">
                        Before GST Rs. {money(inv.total_before_gst)} + GST Rs. {money(inv.gst_charged)} = Grand Total Rs. {money(inv.amount)}
                      </p>
                    )}
                    {inv.items?.length > 0 ? (
                      <ul className="mt-1 space-y-0.5">
                        {inv.items.map((it, i) => (
                          <li key={i} data-testid={`qc-invoice-${inv.id}-item-${i}`} className="text-xs text-[#8A9A91]">
                            <strong className="text-[#1C3F2B]">{it.product_name || "—"}</strong>
                            {it.batch_ref && ` · Batch: ${it.batch_ref}`}
                            {it.description && ` · Test: ${it.description}`}
                          </li>
                        ))}
                        {inv.order_ref_name && <li className="text-xs text-[#8A9A91]">Requested by {inv.order_ref_name}{inv.order_ref_date && ` on ${inv.order_ref_date}`}</li>}
                      </ul>
                    ) : (
                      <>
                        <p className="mt-1 text-xs text-[#8A9A91]">
                          {[inv.product_name, inv.batch_ref].filter(Boolean).join(" · ") || "No product/batch ref"}
                          {inv.order_ref_name && <> · Requested by {inv.order_ref_name}{inv.order_ref_date && ` on ${inv.order_ref_date}`}</>}
                        </p>
                        {inv.description && <p className="mt-0.5 text-xs text-[#8A9A91]">On bank docs: {inv.description}</p>}
                      </>
                    )}
                    {inv.remark && <p className="mt-0.5 text-xs italic text-[#8A9A91]">Remark: {inv.remark}</p>}
                  </div>
                  {(qcEditable || accountsInvEditable) && (
                    <div className="flex shrink-0">
                      <button data-testid={`qc-invoice-edit-${inv.id}`} title={accountsInvEditable && !qcEditable ? "Correct invoice details — totals and the BILL PASSED stamp update automatically" : "Edit invoice details"} onClick={() => setEditInv({ ...inv, currency: c.currency, items: inv.items?.length ? inv.items : [{ product_name: inv.product_name || "", batch_ref: inv.batch_ref || "", description: inv.description || "" }] })} className="rounded-full p-2 text-[#8A9A91] hover:text-[#1C3F2B]">
                        <Pencil className="h-4 w-4" />
                      </button>
                      {qcEditable && (
                        <button onClick={() => act("delinv", () => api.delete(`/cases/${id}/invoices/${inv.id}`), "Invoice removed")}
                          className="rounded-full p-2 text-[#8A9A91] hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </li>
            ))}
            {c.invoices.length === 0 && <p className="text-sm text-[#8A9A91]">No invoices uploaded yet.</p>}
          </ul>
          {qcEditable && (
            <>
              <input ref={uploadRef} type="file" accept=".pdf,image/*" className="hidden" onChange={uploadInvoice} />
              <Button variant="outline" data-testid="qc-invoice-upload" onClick={() => uploadRef.current.click()} className="mt-4 rounded-full">
                <Upload className="mr-1.5 h-4 w-4" /> Upload invoice
              </Button>
            </>
          )}
        </Card>

        <Card title="Vendor (Lab)" sub={vendor ? `${vendor.country}` : ""} testid="qc-vendor-card">
          {vendor && (
            <div className="space-y-1 text-sm">
              <p className="font-semibold">{vendor.name}</p>
              <p className="text-[#8A9A91]">{vendor.address}</p>
              {vendor.contact_person && <p>{vendor.contact_person} · {vendor.phone}</p>}
              {vendor.email && <p>{vendor.email}</p>}
              {(vendor.bank_accounts || []).map((b) => (
                <p key={b.id} className="mt-1 rounded-lg bg-[#F9F8F6] px-3 py-1.5 text-xs">
                  {b.label || b.beneficiary_bank_name} — {b.beneficiary_bank_name}
                  {b.swift_bic && ` · SWIFT ${b.swift_bic}`} {b.iban && ` · IBAN ${b.iban}`} {b.ifsc && ` · IFSC ${b.ifsc}`} · A/c {b.account_no}
                  {b.is_default && <span className="text-[#E5A93D]"> · default</span>}
                </p>
              ))}
              <div className="flex gap-3 pt-2">
                {qcEditable && (
                  <button className="text-xs font-medium text-[#1C3F2B] underline underline-offset-2"
                    data-testid="qc-vendor-add-inline" onClick={() => setVendorDialog({ open: true, initial: null, pickOnly: false })}>
                    + Add new vendor
                  </button>
                )}
                {(canEditVendorRecord || vendorEditBlocked || qcPickOnly) && (
                  <button className="text-xs font-medium text-[#1C3F2B] underline underline-offset-2"
                    data-testid="qc-vendor-edit-inline"
                    onClick={() => {
                      if (vendorEditBlocked) {
                        toast.error("This user does not have permission to change Vendor Name. Please contact QC department to change the Vendor Name");
                        return;
                      }
                      setVendorDialog({ open: true, initial: vendor, pickOnly: qcPickOnly });
                    }}>
                    {qcPickOnly ? "Change vendor" : "Edit vendor"}{vendorEditableByQc && !has("admin") ? " (24h window)" : ""}
                  </button>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>

      <Card
        title="Commercial — Calculation & TDS"
        sub={commercialEditable ? (isExport ? "Select TDS from the master, add a discount if offered, and save." : "Select TDS from the master and save.") : "Prepared by Commercial."}
        testid="qc-commercial-card"
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-3">
            {isExport && (
              <div className="space-y-1.5">
                <Label className="text-xs">Discount % (optional) — applied before TDS</Label>
                <Input data-testid="qc-discount" type="number" step="0.01" value={discount} disabled={!commercialEditable}
                  onChange={(e) => setDiscount(e.target.value)} placeholder="Leave blank if none" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">TDS option ({c.flow}){!isExport ? " — mandatory" : ""}</Label>
              <Select value={tdsId} onValueChange={setTdsId} disabled={!commercialEditable}>
                <SelectTrigger data-testid="qc-tds-select"><SelectValue placeholder="Select TDS" /></SelectTrigger>
                <SelectContent>
                  {tdsOptions.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.label}{t.requires_docs ? " (docs required)" : t.requires_remark ? " (remark required)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {needsTdsDoc && (
              <div className="space-y-1.5">
                <Label className="text-xs">Supporting document for 10.40% TDS {commercialEditable && <span className="ml-0.5 font-semibold text-red-600">*</span>}</Label>
                <Select value={tdsDocType} onValueChange={setTdsDocType} disabled={!commercialEditable}>
                  <SelectTrigger data-testid="qc-tds-doc-type"><SelectValue placeholder="Select document type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRC Certificate">TRC Certificate</SelectItem>
                    <SelectItem value="PAN Card">PAN Card</SelectItem>
                    <SelectItem value="Both of Above">Both of Above</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {(selectedTds?.requires_remark || c.tds_remark) && (
              <div className="space-y-1.5">
                <Label className="text-xs">Internal remark {selectedTds?.requires_remark && commercialEditable ? "(mandatory)" : ""}</Label>
                <Input data-testid="qc-tds-remark" value={tdsRemark} disabled={!commercialEditable}
                  onChange={(e) => setTdsRemark(e.target.value)} placeholder="Reason / internal note" />
              </div>
            )}
            {(selectedTds?.requires_docs || (c.tds_docs || []).length > 0) && (
              <div>
                <Button variant="outline" size="sm" data-testid="qc-tds-docs-open" onClick={() => setTdsDocsOpen(true)} className="rounded-full">
                  Supporting documents ({(c.tds_docs || []).length})
                </Button>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Test / service description {isExport ? "(printed on bank documents)" : ""}</Label>
              <Textarea data-testid="qc-test-desc" rows={2} value={testDesc} disabled={!commercialEditable}
                onChange={(e) => setTestDesc(e.target.value)} placeholder="e.g. Phospholipids Analysis Fee" />
            </div>
            {isExport && (vendor?.bank_accounts || []).length > 1 && (
              <div className="space-y-1.5">
                <Label className="text-xs">Beneficiary bank account</Label>
                <Select value={bankAccId} onValueChange={setBankAccId} disabled={!commercialEditable}>
                  <SelectTrigger data-testid="qc-bank-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {vendor.bank_accounts.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.label || b.beneficiary_bank_name} · {b.account_no}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {commercialEditable && (
              <Button data-testid="qc-commercial-save" onClick={saveCommercial} disabled={busy === "save" || (needsTdsDoc && !tdsDocType)} variant="outline" className="rounded-full">
                Save
              </Button>
            )}
          </div>
          <div className="h-fit rounded-xl bg-[#F9F8F6] p-5" data-testid="qc-calc-panel">
            <dl className="space-y-2 text-sm">
              {isExport ? (
                <>
                  <div className="flex justify-between"><dt className="text-[#8A9A91]">Gross Invoice Total</dt><dd className="font-medium">{c.currency} {money(commercialEditable && calc ? calc.gross : c.gross)}</dd></div>
                  <div className="flex justify-between"><dt className="text-[#8A9A91]">Discount {discount ? `(${discount}%)` : ""}</dt><dd>− {c.currency} {money((commercialEditable && calc ? calc.gross - calc.subtotal : c.gross - c.subtotal) || 0)}</dd></div>
                  <div className="flex justify-between border-t border-[#E8E6E1] pt-2"><dt className="text-[#8A9A91]">Subtotal after discount</dt><dd className="font-medium">{c.currency} {money(commercialEditable && calc ? calc.subtotal : c.subtotal)}</dd></div>
                  <div className="flex justify-between"><dt className="text-[#8A9A91]">TDS {(commercialEditable && calc?.opt?.rate != null) ? `(${calc.opt.rate}%)` : c.tds_label ? `(${c.tds_label})` : ""}</dt><dd>− {c.currency} {money(commercialEditable && calc ? calc.tds : c.tds_amount)}</dd></div>
                  {(c.tds_doc_type || (commercialEditable && tdsDocType)) && (
                    <div className="flex justify-between"><dt className="text-[#8A9A91]">TDS supporting document</dt><dd data-testid="qc-tds-doc-value" className="font-medium">{commercialEditable && tdsDocType ? tdsDocType : c.tds_doc_type}</dd></div>
                  )}
                  <div className="flex justify-between border-t border-[#1C3F2B]/20 pt-2 text-base font-semibold">
                    <dt>FINAL NET REMITTANCE VALUE</dt>
                    <dd data-testid="qc-net-amount">{c.currency} {money(commercialEditable && calc ? calc.net : c.net)}</dd>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between"><dt className="text-[#8A9A91]">Total Amount (before GST)</dt><dd className="font-medium">Rs. {money(commercialEditable && calc ? calc.base : domBase)}</dd></div>
                  <div className="flex justify-between"><dt className="text-[#8A9A91]">GST</dt><dd data-testid="qc-calc-gst">+ Rs. {money(commercialEditable && calc ? calc.gst : domGst)}</dd></div>
                  <div className="flex justify-between border-t border-[#E8E6E1] pt-2"><dt className="text-[#8A9A91]">Final Amount</dt><dd className="font-medium">Rs. {money(commercialEditable && calc ? calc.base + calc.gst : domBase + domGst)}</dd></div>
                  <div className="flex justify-between"><dt className="text-[#8A9A91]">TDS {(commercialEditable && calc?.opt?.rate != null) ? `(${calc.opt.label})` : c.tds_label ? `(${c.tds_label})` : ""}</dt><dd>− Rs. {money(commercialEditable && calc ? calc.tds : c.tds_amount)}</dd></div>
                  <div className="flex justify-between border-t border-[#1C3F2B]/20 pt-2 text-base font-semibold">
                    <dt>NET PAYABLE</dt>
                    <dd data-testid="qc-net-amount">Rs. {money(commercialEditable && calc ? calc.net : c.net)}</dd>
                  </div>
                </>
              )}
            </dl>
          </div>
        </div>
      </Card>

      {isExport && ["commercial", "unit_head", "accounts", "closed"].includes(c.stage) && (
        <Card title="Bank Documents" sub="Form A2, Bank Letter and FEMA Declaration — filled into your official templates. Click to view in the browser; downloads are PDF, ready to print/sign for the bank." testid="qc-docs-card">
          {docLinks.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {docLinks.map(([k, label]) => (
                <a key={k} data-testid={`qc-doc-${k}`} href={fileUrl(c.docs[k])} target="_blank" rel="noreferrer"
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium ${
                    k === "combined" ? "bg-[#1C3F2B] text-[#F9F8F6]" : "border border-[#E8E6E1] text-[#1C3F2B] hover:bg-[#F9F8F6]"
                  }`}>
                  <Download className="h-4 w-4" /> {label}
                </a>
              ))}
              <span className="self-center text-xs text-[#8A9A91]">Generated by {c.docs.generated_by} · {new Date(c.docs.generated_at).toLocaleString("en-IN")}</span>
            </div>
          ) : (
            <p className="text-sm text-[#8A9A91]">Not generated yet.</p>
          )}
          {canGenerate && (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button data-testid="qc-docs-generate" onClick={() => act("gen", () => api.post(`/cases/${id}/generate-docs`), "Documents generated")}
                disabled={busy === "gen"} className="rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
                {busy === "gen" ? "Generating…" : docLinks.length ? "Regenerate documents" : "Generate documents"}
              </Button>
              <Button variant="outline" data-testid="qc-docs-edit" onClick={() => setDocsEditOpen(true)} className="rounded-full">
                Edit document fields
              </Button>
            </div>
          )}
        </Card>
      )}

      {!isExport && c.stamped_invoice_path && (
        <Card title="Stamped Invoice" sub="Duplicate of the lab invoice with the BILL PASSED stamp. The original file is never altered." testid="qc-stamp-card">
          <a data-testid="qc-stamped-invoice" href={fileUrl(c.stamped_invoice_path)} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1C3F2B] px-4 py-2 text-sm font-medium text-[#F9F8F6]">
            <Download className="h-4 w-4" /> View stamped invoice
          </a>
          <p className="mt-2 text-xs text-[#8A9A91]">
            SR NO. {c.serial_no} · Checked by {c.checked_by} · {c.passed_by ? `Passed by ${c.passed_by}` : "Awaiting Unit Head sign-off"}
          </p>
        </Card>
      )}

      {(c.stage === "closed" || c.stage === "accounts") && Object.keys(c.closing_docs || {}).length > 0 && (
        <Card title="Closing documents" testid="qc-closing-docs-card">
          <div className="flex flex-wrap gap-2">
            {c.closing_docs.form_145 && (
              <a data-testid="qc-doc-form145" href={fileUrl(c.closing_docs.form_145.storage_path)} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E8E6E1] px-4 py-2 text-sm font-medium hover:bg-[#F9F8F6]">
                <Download className="h-4 w-4" /> Form 145
              </a>
            )}
            {c.closing_docs.form_146 && (
              <a data-testid="qc-doc-form146" href={fileUrl(c.closing_docs.form_146.storage_path)} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E8E6E1] px-4 py-2 text-sm font-medium hover:bg-[#F9F8F6]">
                <Download className="h-4 w-4" /> Form 146
              </a>
            )}
            {c.closing_docs.ca_certificate && (
              <a data-testid="qc-doc-ca" href={fileUrl(c.closing_docs.ca_certificate.storage_path)} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E8E6E1] px-4 py-2 text-sm font-medium hover:bg-[#F9F8F6]">
                <Download className="h-4 w-4" /> CA Certificate
              </a>
            )}
            {c.closing_docs.bank_payment_advice && (
              <a data-testid="qc-doc-advice" href={fileUrl(c.closing_docs.bank_payment_advice.storage_path)} target="_blank" rel="noreferrer"
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium ${
                  c.stage === "closed" && has("commercial") ? "bg-[#E5A93D] text-[#1C3F2B]" : "border border-[#E8E6E1] hover:bg-[#F9F8F6]"
                }`}>
                <Download className="h-4 w-4" /> Bank Payment Advice {c.stage === "closed" && has("commercial") ? "(send to vendor)" : ""}
              </a>
            )}
          </div>
        </Card>
      )}

      {c.payment && ["accounts", "closed"].includes(c.stage) && (
        <Card title="Payment details" testid="qc-payment-card">
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <div className="flex gap-2"><dt className="text-[#8A9A91]">Payment Date:</dt><dd data-testid="qc-payment-date" className="font-medium">{c.payment.date}</dd></div>
            <div className="flex gap-2"><dt className="text-[#8A9A91]">Cheque / UTR #:</dt><dd data-testid="qc-payment-ref" className="font-medium">{c.payment.ref}</dd></div>
            <div className="flex gap-2"><dt className="text-[#8A9A91]">Payment Amount:</dt><dd data-testid="qc-payment-amount" className="font-medium">INR {money(c.payment.amount)}</dd></div>
            <div className="flex gap-2"><dt className="text-[#8A9A91]">Recorded by:</dt><dd className="font-medium">{c.payment.by}</dd></div>
            {c.payment.remark && (
              <div className="flex gap-2 sm:col-span-2"><dt className="text-[#8A9A91]">Remark:</dt><dd data-testid="qc-payment-remark" className="font-medium">{c.payment.remark}</dd></div>
            )}
          </dl>
        </Card>
      )}

      <Card title="Action" testid="qc-action-card">
        {qcEditable && (
          <Button data-testid="qc-action-transfer" disabled={busy === "transfer"}
            onClick={() => act("transfer", () => api.post(`/cases/${id}/transfer`), "Transferred to Commercial")}
            className="rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
            Confirm &amp; Transfer to Commercial
          </Button>
        )}
        {canApprove && isExport && (
          <Button data-testid="qc-action-approve" disabled={busy === "approve"}
            onClick={() => act("approve", () => api.post(`/cases/${id}/approve`), "Sent to Unit Head")}
            className="rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
            Approved for Authorisation → Unit Head
          </Button>
        )}
        {canApprove && !isExport && (
          <Button data-testid="qc-action-authorise" disabled={busy === "authorise"}
            onClick={() => act("authorise", () => api.post(`/cases/${id}/authorise`), "Authorised — stamped invoice sent to Unit Head")}
            className="rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
            Authorise &amp; Stamp → Unit Head
          </Button>
        )}
        {canUnitHeadAct && isExport && (
          <Button data-testid="qc-action-auth-accounts" disabled={busy === "uh"}
            onClick={() => act("uh", () => api.post(`/cases/${id}/authorise-for-accounts`), "Sent to Accounts")}
            className="rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
            Authorised for Accounts
          </Button>
        )}
        {canUnitHeadAct && !isExport && (
          <div>
            {!user.has_signature && (
              <p className="mb-3 text-sm font-medium text-[#7a5b12]">
                Upload your scanned signature in <Link to="/qc/settings" className="underline">Settings</Link> before passing bills.
              </p>
            )}
            <Button data-testid="qc-action-pass" disabled={busy === "pass" || !user.has_signature}
              onClick={() => act("pass", () => api.post(`/cases/${id}/pass-to-accounts`), "Signed and passed to Accounts")}
              className="rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
              Pass to Accounts (applies your signature)
            </Button>
          </div>
        )}
        {canClose && (
          <div className="space-y-3">
            {isExport ? (
              <div className="space-y-4">
                <div className="space-y-3 rounded-xl border border-[#E8E6E1] p-4" data-testid="qc-close-forms">
                  <div className="flex items-center justify-between">
                    <Label>CA Certificate — Form 145 & Form 146 (both mandatory)</Label>
                    {formsSaved && (
                      <span data-testid="qc-close-forms-saved" className="rounded-full bg-[#1C3F2B]/10 px-2.5 py-0.5 text-[11px] font-semibold text-[#1C3F2B]">
                        Saved ✓
                      </span>
                    )}
                  </div>
                  {formsSaved && (
                    <p className="text-xs text-[#8A9A91]">
                      Form 145 ({c.closing_docs.form_145.file_name}) and Form 146 ({c.closing_docs.form_146.file_name}) saved
                      {c.closing_docs.form_146.uploaded_by ? ` by ${c.closing_docs.form_146.uploaded_by}` : ""}. Upload both again only if you need to replace them.
                    </p>
                  )}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Form 145</Label>
                      <Input data-testid="qc-close-form145" type="file" accept=".pdf,image/*" ref={caRef} onChange={() => forceCloseCheck((x) => x + 1)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Form 146</Label>
                      <Input data-testid="qc-close-form146" type="file" accept=".pdf,image/*" ref={ca2Ref} onChange={() => forceCloseCheck((x) => x + 1)} />
                    </div>
                  </div>
                  <Button data-testid="qc-close-forms-save" variant="outline" className="rounded-full"
                    disabled={busy === "saveforms" || !(caRef.current?.files?.length) || !(ca2Ref.current?.files?.length)}
                    onClick={saveClosingForms}>
                    Save forms — you can close the case later
                  </Button>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Bank Payment Advice (mandatory to close)</Label>
                  <Input data-testid="qc-close-advice" type="file" accept=".pdf,image/*" ref={adviceRef} onChange={() => forceCloseCheck((x) => x + 1)} />
                  {!formsSaved && (
                    <p className="text-xs text-amber-700">Save Form 145 and Form 146 first — only then can the case be closed.</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3" data-testid="qc-payment-form">
                <p className="text-xs text-[#8A9A91]">
                  Approved Net Payable: <strong className="text-[#1C3F2B]">INR {money(netPayable)}</strong> — enter the payment details to close this case.
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Payment Date (mandatory)</Label>
                    <Input data-testid="qc-pay-date" value={payForm.date} placeholder="DD/MM/YYYY"
                      onChange={(e) => setPayForm({ ...payForm, date: e.target.value })}
                      onBlur={(e) => setPayForm((p) => ({ ...p, date: autoDate(e.target.value, "/") }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Cheque / UTR # (mandatory)</Label>
                    <Input data-testid="qc-pay-ref" value={payForm.ref} placeholder="e.g. UTR SBIN526123456789"
                      onChange={(e) => setPayForm({ ...payForm, ref: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Payment Amount (mandatory)</Label>
                    <Input data-testid="qc-pay-amount" type="number" min="0" step="0.01" value={payForm.amount} placeholder={String(netPayable)}
                      onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Remark {payMismatch ? "(mandatory — min 15 characters)" : "(optional)"}</Label>
                    <Input data-testid="qc-pay-remark" value={payForm.remark} placeholder="Reason / note for this payment"
                      onChange={(e) => setPayForm({ ...payForm, remark: e.target.value })} />
                  </div>
                </div>
                {payMismatch && (
                  <p data-testid="qc-pay-mismatch-warning" className={`text-xs font-medium ${remarkShort ? "text-red-700" : "text-amber-700"}`}>
                    Payment amount {money(payAmount)} does not match the approved Net Payable {money(netPayable)}.
                    {remarkShort ? ` Please write a remark of at least 15 characters (${payForm.remark.trim().length}/15).` : " Remark recorded."}
                  </p>
                )}
              </div>
            )}
            <Button data-testid="qc-action-close"
              disabled={busy === "close" || (isExport ? !(adviceRef.current?.files?.length) || !formsSaved : !payValid)}
              onClick={closeCase} className="rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
              Close Case
            </Button>
          </div>
        )}
        {c.stage === "closed" && <p className="text-sm font-medium text-[#1C3F2B]">This case is closed. No further action needed.</p>}
        {!qcEditable && !canApprove && c.stage !== "closed" && !canUnitHeadAct && !canClose && (
          <p className="text-sm text-[#8A9A91]">Waiting on {STAGE_LABELS[c.stage].replace("With ", "")} — no action available for your role right now.</p>
        )}
        {canDeleteCase && (
          <div className="mt-4 border-t border-[#E8E6E1] pt-4">
            <Button
              variant="outline"
              data-testid="qc-action-delete"
              disabled={busy === "delete"}
              onClick={async () => {
                const msg = has("admin")
                  ? `Delete case ${c.case_no} permanently? Admin can delete any case, at any stage.`
                  : `Delete case ${c.case_no}? You can only delete cases you created within 24 hours, while still with QC.`;
                if (!window.confirm(msg)) return;
                setBusy("delete");
                try {
                  await api.delete(`/cases/${id}`);
                  toast.success(`Case ${c.case_no} deleted`);
                  navigate(`/qc/invoicing/${c.flow}`);
                } catch (e) {
                  toast.error(apiError(e));
                  setBusy("");
                }
              }}
              className="rounded-full border-red-200 text-red-700 hover:bg-red-50"
            >
              <Trash2 className="mr-1.5 h-4 w-4" /> Delete this case
            </Button>
            {!has("admin") && (
              <p className="mt-2 text-xs text-[#8A9A91]">Available for 24 hours after you create a case, while it is still with QC.</p>
            )}
          </div>
        )}
      </Card>

      <Card title="History" testid="qc-history-card">
        <ul className="space-y-2">
          {(c.stage_history || []).map((h, i) => (
            <li key={i} className="flex items-center gap-3 text-sm">
              <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${STAGE_STYLES[h.stage]}`}>{STAGE_LABELS[h.stage]}</span>
              <span className="text-[#8A9A91]">{h.by_name} · {new Date(h.at).toLocaleString("en-IN")}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Dialog open={!!editInv} onOpenChange={(o) => !o && setEditInv(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid="qc-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Invoice details</DialogTitle>
          </DialogHeader>
          {editInv && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1"><Label className="text-xs">Invoice No.{REQ}</Label>
                <Input data-testid="qc-inv-no" value={editInv.invoice_no} onChange={(e) => setEditInv({ ...editInv, invoice_no: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">Invoice Date{REQ}</Label>
                <Input data-testid="qc-inv-date" value={editInv.invoice_date} placeholder="DD/MM/YYYY" onChange={(e) => setEditInv({ ...editInv, invoice_date: e.target.value })}
                  onBlur={(e) => setEditInv((p) => ({ ...p, invoice_date: autoDate(e.target.value, "/") }))} /></div>
              {!isExport && (
                <>
                  <div className="space-y-1"><Label className="text-xs">Total Amount (before GST){REQ}</Label>
                    <Input data-testid="qc-inv-base" type="number" step="0.01" value={editInv.total_before_gst ?? ""} onChange={(e) => setEditInv({ ...editInv, total_before_gst: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">GST Charged{REQ}</Label>
                    <Input data-testid="qc-inv-gst" type="number" step="0.01" value={editInv.gst_charged ?? ""} onChange={(e) => setEditInv({ ...editInv, gst_charged: e.target.value })} /></div>
                </>
              )}
              {isExport ? (
                <>
                  <div className="space-y-1"><Label className="text-xs">Currency{REQ}</Label>
                    <Select value={editInv.currency || c.currency} onValueChange={(v) => setEditInv({ ...editInv, currency: v })}>
                      <SelectTrigger data-testid="qc-inv-currency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["USD", "EUR", "GBP"].map((cur) => <SelectItem key={cur} value={cur}>{cur}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                  <div className="space-y-1"><Label className="text-xs">Amount{REQ}</Label>
                    <Input data-testid="qc-inv-amount" type="number" step="0.01" value={editInv.amount ?? ""} onChange={(e) => setEditInv({ ...editInv, amount: e.target.value })} /></div>
                </>
              ) : (
                <div className="space-y-1"><Label className="text-xs">Grand Total (incl. GST){REQ}</Label>
                  <Input data-testid="qc-inv-amount" type="number" step="0.01" value={editInv.amount ?? ""} onChange={(e) => setEditInv({ ...editInv, amount: e.target.value })} /></div>
              )}
              <div className="space-y-2 sm:col-span-2" data-testid="qc-inv-items">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Add Product Details on this Invoice{REQ}</Label>
                  <button type="button" data-testid="qc-inv-item-add"
                    onClick={() => setEditInv({ ...editInv, items: [...editInv.items, { product_name: "", batch_ref: "", description: "" }] })}
                    className="inline-flex items-center gap-1 rounded-full border border-[#1C3F2B] px-2.5 py-1 text-xs font-medium text-[#1C3F2B] hover:bg-[#1C3F2B] hover:text-[#F9F8F6]">
                    <Plus className="h-3 w-3" /> Add Products
                  </button>
                </div>
                {editInv.items.map((it, i) => (
                  <div key={i} data-testid={`qc-inv-item-${i}`} className="grid grid-cols-1 gap-2 rounded-xl border border-[#E8E6E1] p-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end">
                    <div className="space-y-1">
                      <Label className="text-xs">Product Name{REQ}</Label>
                      <Select value={it.product_name || ""} onValueChange={(v) => setEditInv({ ...editInv, items: editInv.items.map((x, j) => (j === i ? { ...x, product_name: v } : x)) })}>
                        <SelectTrigger data-testid={`qc-inv-item-product-${i}`}><SelectValue placeholder="Select product" /></SelectTrigger>
                        <SelectContent>
                          {products.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Batch No. (optional)</Label>
                      <Input data-testid={`qc-inv-item-batch-${i}`} value={it.batch_ref || ""} placeholder="e.g. B-2401"
                        onChange={(e) => setEditInv({ ...editInv, items: editInv.items.map((x, j) => (j === i ? { ...x, batch_ref: e.target.value } : x)) })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Test Name{REQ}</Label>
                      <Input data-testid={`qc-inv-item-test-${i}`} value={it.description || ""} placeholder="e.g. Purity analysis"
                        onChange={(e) => setEditInv({ ...editInv, items: editInv.items.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)) })} />
                    </div>
                    {editInv.items.length > 1 && (
                      <button type="button" data-testid={`qc-inv-item-remove-${i}`} title="Remove this product"
                        onClick={() => setEditInv({ ...editInv, items: editInv.items.filter((_, j) => j !== i) })}
                        className="rounded-full p-2 text-[#8A9A91] hover:text-red-700">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="space-y-1"><Label className="text-xs">Order/Reference Name (internal){REQ}</Label>
                <Input data-testid="qc-inv-order-name" value={editInv.order_ref_name} onChange={(e) => setEditInv({ ...editInv, order_ref_name: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">Order/Reference Date (internal){REQ}</Label>
                <Input data-testid="qc-inv-order-date" value={editInv.order_ref_date} placeholder="DD/MM/YYYY" onChange={(e) => setEditInv({ ...editInv, order_ref_date: e.target.value })}
                  onBlur={(e) => setEditInv((p) => ({ ...p, order_ref_date: autoDate(e.target.value, "/") }))} /></div>
              <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Remark (internal, e.g. why invoice is uploaded late?)</Label>
                <Input data-testid="qc-inv-remark" value={editInv.remark || ""} placeholder="Anything else related to this invoice"
                  onChange={(e) => setEditInv({ ...editInv, remark: e.target.value })} /></div>
              <Button data-testid="qc-inv-save" onClick={saveInvoice} className="rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a] sm:col-span-2">
                Save invoice
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={tdsDocsOpen} onOpenChange={setTdsDocsOpen}>
        <DialogContent data-testid="qc-tds-docs-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">TDS supporting documents</DialogTitle>
          </DialogHeader>
          <ul className="space-y-2">
            {(c.tds_docs || []).map((d) => (
              <li key={d.id} className="flex items-center justify-between rounded-xl border border-[#E8E6E1] px-3 py-2 text-sm">
                <a href={fileUrl(d.storage_path)} target="_blank" rel="noreferrer" className="font-medium text-[#1C3F2B] underline underline-offset-2">{d.file_name}</a>
                <span className="mx-2 flex-1 text-xs text-[#8A9A91]">{d.remark}</span>
                {commercialEditable && (
                  <button onClick={() => act("deltds", () => api.delete(`/cases/${id}/tds-docs/${d.id}`))} className="text-[#8A9A91] hover:text-red-700">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </li>
            ))}
            {(c.tds_docs || []).length === 0 && <p className="text-sm text-[#8A9A91]">No documents yet — at least one document + remark is required.</p>}
          </ul>
          {commercialEditable && (
            <div className="space-y-2 border-t border-[#E8E6E1] pt-4">
              <Input type="file" ref={tdsFileRef} data-testid="qc-tds-doc-file" />
              <Input placeholder="Remark for this document" value={tdsRemarkNew} data-testid="qc-tds-doc-remark" onChange={(e) => setTdsRemarkNew(e.target.value)} />
              <Button onClick={addTdsDoc} data-testid="qc-tds-doc-add" variant="outline" className="rounded-full">
                <Plus className="mr-1 h-4 w-4" /> Add document
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={docsEditOpen} onOpenChange={setDocsEditOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid="qc-docs-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Edit document fields</DialogTitle>
          </DialogHeader>
          <div className="mb-2 flex flex-wrap gap-2">
            {DOC_TEMPLATES.map(([k, label]) => (
              <button
                key={k}
                data-testid={`qc-doc-pick-${k}`}
                onClick={() => setDocChoice(k)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  docChoice === k ? "bg-[#1C3F2B] text-[#F9F8F6]" : "border border-[#E8E6E1] text-[#8A9A91] hover:text-[#1C3F2B]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {DOC_FIELD_LISTS[docChoice].map((f) => {
              const overridden = !!(data.case.doc_overrides || {})[f];
              return (
                <div key={f} className={`space-y-1 ${WIDE_FIELDS.includes(f) ? "sm:col-span-2" : ""}`}>
                  <Label className="text-xs">
                    {FIELD_LABELS[f]}
                    {overridden && <span className="ml-2 rounded-full bg-[#E5A93D]/20 px-2 py-0.5 text-[10px] font-semibold text-[#7a5b12]">edited</span>}
                  </Label>
                  {WIDE_FIELDS.includes(f) ? (
                    <Textarea rows={2} data-testid={`qc-ov-${f}`} value={docForm[f] ?? ""}
                      onChange={(e) => setDocForm({ ...docForm, [f]: e.target.value })} />
                  ) : (
                    <Input data-testid={`qc-ov-${f}`} value={docForm[f] ?? ""} placeholder={f.includes("date") ? "DD/MM/YYYY" : undefined}
                      onChange={(e) => setDocForm({ ...docForm, [f]: e.target.value })}
                      onBlur={f.includes("date") ? (e) => setDocForm((p) => ({ ...p, [f]: autoDate(e.target.value, "/") })) : undefined} />
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-xs text-[#8A9A91]">
            Fields are pre-filled with the current data. Saving clears the generated PDFs — click “Regenerate documents” afterwards. Resetting a field to its original value removes the override.
          </p>
          <Button data-testid="qc-ov-save" onClick={saveOverrides} disabled={busy === "ov"}
            className="w-full rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
            Save {DOC_TEMPLATES.find(([k]) => k === docChoice)?.[1]} fields
          </Button>
        </DialogContent>
      </Dialog>

      <VendorFormDialog
        open={vendorDialog.open}
        initial={vendorDialog.initial}
        pickOnly={vendorDialog.pickOnly || false}
        onOpenChange={(o) => setVendorDialog((d) => ({ ...d, open: o }))}
        onSaved={async (v) => {
          setVendorDialog({ open: false, initial: null, pickOnly: false });
          // If the user picked a different vendor while editing from this case,
          // re-point the case to that vendor (QC/Admin, only while with QC)
          const canRepoint = c.stage === "qc" && (has("admin") || has("qc"));
          if (v?.id && v.id !== c.vendor_id) {
            if (canRepoint) {
              try {
                await api.put(`/cases/${id}`, { vendor_id: v.id });
                toast.success(`Case vendor changed to ${v.name}`);
              } catch (e) {
                toast.error(apiError(e));
              }
            } else {
              toast.info("Vendor record updated — the case keeps its current vendor once it has left QC.");
            }
          }
          load();
        }}
      />
    </div>
  );
}
